import { type ReactNode, lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { AuthProvider } from '@/context/AuthContext'
import { CompanyProvider } from '@/context/CompanyContext'
import { useCompany } from '@/hooks/useCompany'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RequirePermission } from '@/components/ui/Can'
import { LandingPage } from '@/pages/LandingPage'
import { ThemeProvider } from '@/context/ThemeContext'
import { ConnectionStatusIndicator } from '@/components/ConnectionStatusIndicator'

import './App.css'

const BpoFinanceiroPage = lazy(() => import('@/pages/marketing/BpoFinanceiroPage').then(m => ({ default: m.BpoFinanceiroPage })))
const BpoSubservicePage = lazy(() => import('@/pages/marketing/BpoSubservicePage').then(m => ({ default: m.BpoSubservicePage })))
const AboutUsPage = lazy(() => import('@/pages/marketing/AboutUsPage').then(m => ({ default: m.AboutUsPage })))
const PlatformPage = lazy(() => import('@/pages/marketing/PlatformPage').then(m => ({ default: m.PlatformPage })))
const BpoSegmentPage = lazy(() => import('@/pages/marketing/BpoSegmentPage').then(m => ({ default: m.BpoSegmentPage })))
const BlogIndexPage = lazy(() => import('@/pages/marketing/BlogIndexPage').then(m => ({ default: m.BlogIndexPage })))
const BlogPostPage = lazy(() => import('@/pages/marketing/BlogPostPage').then(m => ({ default: m.BlogPostPage })))

const AppLayout = lazy(() => import('@/layouts/AppLayout').then(m => ({ default: m.AppLayout })))
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage').then(m => ({ default: m.CheckoutPage })))
const PrivacyPolicyPage = lazy(() => import('@/pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })))
const TermsOfUsePage = lazy(() => import('@/pages/TermsOfUsePage').then(m => ({ default: m.TermsOfUsePage })))
const AccountDeletionPublicPage = lazy(() => import('@/pages/AccountDeletionPublicPage').then(m => ({ default: m.AccountDeletionPublicPage })))
const AccountDeletionConfirmPage = lazy(() => import('@/pages/AccountDeletionConfirmPage').then(m => ({ default: m.AccountDeletionConfirmPage })))
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })))
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })))
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })))
const DashboardPage = lazy(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })))
import { CompaniesPage } from '@/pages/CompaniesPage'
import { CompanyDetailPage } from '@/pages/CompanyDetailPage'
import { CompanyFormPage } from '@/pages/CompanyFormPage'
const SuperAdminDashboardPage = lazy(() => import('@/pages/SuperAdminDashboardPage').then(m => ({ default: m.SuperAdminDashboardPage })))
const OnboardingManagementPage = lazy(() => import('@/pages/OnboardingManagementPage').then(m => ({ default: m.OnboardingManagementPage })))
const OnboardingDetailPage = lazy(() => import('@/pages/OnboardingDetailPage').then(m => ({ default: m.OnboardingDetailPage })))
const OnboardingFormPage = lazy(() => import('@/pages/public/OnboardingFormPage').then(m => ({ default: m.OnboardingFormPage })))

const UsersPage = lazy(() => import('@/pages/UsersPage').then(m => ({ default: m.UsersPage })))
const UserFormPage = lazy(() => import('@/pages/UserFormPage').then(m => ({ default: m.UserFormPage })))
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage').then(m => ({ default: m.CategoriesPage })))
const CompanyCategoriesPage = lazy(() => import('@/pages/CompanyCategoriesPage').then(m => ({ default: m.CompanyCategoriesPage })))
const ClientsPage = lazy(() => import('@/pages/ClientsPage').then(m => ({ default: m.ClientsPage })))
const ClientFormPage = lazy(() => import('@/pages/ClientFormPage').then(m => ({ default: m.ClientFormPage })))
const MovementsPage = lazy(() => import('@/pages/MovementsPage').then(m => ({ default: m.MovementsPage })))
const MovementFormPage = lazy(() => import('@/pages/MovementFormPage').then(m => ({ default: m.MovementFormPage })))
const AssetsPage = lazy(() => import('@/pages/AssetsPage').then(m => ({ default: m.AssetsPage })))
const AssetFormPage = lazy(() => import('@/pages/AssetFormPage').then(m => ({ default: m.AssetFormPage })))
const CommissionsPage = lazy(() => import('@/pages/CommissionsPage').then(m => ({ default: m.CommissionsPage })))
const InvoicesPage = lazy(() => import('@/pages/InvoicesPage').then(m => ({ default: m.InvoicesPage })))
const CommissionFormPage = lazy(() => import('@/pages/CommissionFormPage').then(m => ({ default: m.CommissionFormPage })))
const CalendarPage = lazy(() => import('@/pages/CalendarPage').then(m => ({ default: m.CalendarPage })))
const ReconciliationPage = lazy(() => import('@/pages/ReconciliationPage').then(m => ({ default: m.ReconciliationPage })))
const ReconciliationV2Page = lazy(() => import('@/pages/ReconciliationV2Page').then(m => ({ default: m.ReconciliationV2Page })))
const InvestorDashboardPage = lazy(() => import('@/pages/InvestorDashboardPage').then(m => ({ default: m.InvestorDashboardPage })))
const ServicesPage = lazy(() => import('@/pages/ServicesPage').then(m => ({ default: m.ServicesPage })))
const ServiceFormPage = lazy(() => import('@/pages/ServiceFormPage').then(m => ({ default: m.ServiceFormPage })))
const ContractsPage = lazy(() => import('@/pages/ContractsPage').then(m => ({ default: m.ContractsPage })))
const ReportsPage = lazy(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })))
const ReconciledReportPage = lazy(() => import('@/pages/ReconciledReportPage').then(m => ({ default: m.ReconciledReportPage })))
const MensalPage = lazy(() => import('@/pages/MensalPage').then(m => ({ default: m.MensalPage })))
const CashFlowPage = lazy(() => import('@/pages/CashFlowPage').then(m => ({ default: m.CashFlowPage })))
const BankAccountsPage = lazy(() => import('@/pages/BankAccountsPage').then(m => ({ default: m.BankAccountsPage })))
const BankAccountFormPage = lazy(() => import('@/pages/BankAccountFormPage').then(m => ({ default: m.BankAccountFormPage })))
const BankStatementsPage = lazy(() => import('@/pages/BankStatementsPage').then(m => ({ default: m.BankStatementsPage })))
const PaymentMethodsPage = lazy(() => import('@/pages/PaymentMethodsPage').then(m => ({ default: m.PaymentMethodsPage })))
const PaymentMethodFormPage = lazy(() => import('@/pages/PaymentMethodFormPage').then(m => ({ default: m.PaymentMethodFormPage })))
const CategoryFormPage = lazy(() => import('@/pages/CategoryFormPage').then(m => ({ default: m.CategoryFormPage })))
const CompanyCategoryFormPage = lazy(() => import('@/pages/CompanyCategoryFormPage').then(m => ({ default: m.CompanyCategoryFormPage })))
const PlansPage = lazy(() => import('@/pages/PlansPage').then(m => ({ default: m.PlansPage })))
const PlanFormPage = lazy(() => import('@/pages/PlanFormPage').then(m => ({ default: m.PlanFormPage })))
const BillingDashboardPage = lazy(() => import('@/pages/BillingDashboardPage').then(m => ({ default: m.BillingDashboardPage })))
const AvanttiDashboardPage = lazy(() => import('@/pages/AvanttiDashboardPage').then(m => ({ default: m.AvanttiDashboardPage })))
const CostCentersPage = lazy(() => import('@/pages/CostCentersPage').then(m => ({ default: m.CostCentersPage })))
const CostCenterFormPage = lazy(() => import('@/pages/CostCenterFormPage').then(m => ({ default: m.CostCenterFormPage })))
const CostCenterDetailsPage = lazy(() => import('@/pages/CostCenterDetailsPage').then(m => ({ default: m.CostCenterDetailsPage })))
const ClientDetailsPage = lazy(() => import('@/pages/ClientDetailsPage').then(m => ({ default: m.ClientDetailsPage })))
const BankAccountDetailsPage = lazy(() => import('@/pages/BankAccountDetailsPage').then(m => ({ default: m.BankAccountDetailsPage })))
const PaymentMethodDetailsPage = lazy(() => import('@/pages/PaymentMethodDetailsPage').then(m => ({ default: m.PaymentMethodDetailsPage })))
const PartnerDetailsPage = lazy(() => import('@/pages/PartnerDetailsPage').then(m => ({ default: m.PartnerDetailsPage })))
const ServiceDetailsPage = lazy(() => import('@/pages/ServiceDetailsPage').then(m => ({ default: m.ServiceDetailsPage })))
const ContactPage = lazy(() => import('@/pages/ContactPage').then(m => ({ default: m.ContactPage })))
const ContactSubmissionsPage = lazy(() => import('@/pages/ContactSubmissionsPage').then(m => ({ default: m.ContactSubmissionsPage })))

// HR (Recursos Humanos)
const EmployeeListPage = lazy(() => import('./pages/hr/EmployeeListPage').then(m => ({ default: m.EmployeeListPage })))
const EmployeeFormPage = lazy(() => import('./pages/hr/EmployeeFormPage').then(m => ({ default: m.EmployeeFormPage })))
const EmployeeDetailsPage = lazy(() => import('./pages/hr/EmployeeDetailsPage').then(m => ({ default: m.EmployeeDetailsPage })))

const ProfilePage = lazy(() => import('@/pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const EmailSettingsPage = lazy(() => import('@/pages/EmailSettingsPage').then(m => ({ default: m.EmailSettingsPage })))
const WhatsappSettingsPage = lazy(() => import('@/pages/WhatsappSettingsPage').then(m => ({ default: m.WhatsappSettingsPage })))
const BrandingPage = lazy(() => import('@/pages/BrandingPage').then(m => ({ default: m.BrandingPage })))
const LegalSettingsPage = lazy(() => import('@/pages/LegalSettingsPage').then(m => ({ default: m.LegalSettingsPage })))
const AsaasSettingsPage = lazy(() => import('@/pages/AsaasSettingsPage'))
const PendingPayoutsPage = lazy(() => import('@/pages/PendingPayoutsPage').then(m => ({ default: m.PendingPayoutsPage })))
const IntegrationLogsPage = lazy(() => import('@/pages/IntegrationLogsPage'))
const SystemJobLogsPage = lazy(() => import('@/pages/SystemJobLogsPage'))
const SystemDocumentsPage = lazy(() => import('@/pages/SystemDocumentsPage'))
const UsersAccessPage = lazy(() => import('@/pages/admin/UsersAccessPage').then(m => ({ default: m.UsersAccessPage })))
const MasterSchemaUpdatesPage = lazy(() => import('@/pages/MasterSchemaUpdatesPage').then(m => ({ default: m.MasterSchemaUpdatesPage })))
const MilestonesPage = lazy(() => import('@/pages/MilestonesPage').then(m => ({ default: m.MilestonesPage })))
const MilestoneFormPage = lazy(() => import('@/pages/MilestoneFormPage').then(m => ({ default: m.MilestoneFormPage })))
const PartnersPage = lazy(() => import('@/pages/PartnersPage').then(m => ({ default: m.PartnersPage })))
const PartnerFormPage = lazy(() => import('@/pages/PartnerFormPage').then(m => ({ default: m.PartnerFormPage })))
const LeadsPage = lazy(() => import('@/pages/LeadsPage').then(m => ({ default: m.LeadsPage })))
const LeadFormPage = lazy(() => import('@/pages/LeadFormPage').then(m => ({ default: m.LeadFormPage })))
const LeadsMetricsPage = lazy(() => import('@/pages/admin/LeadsMetricsPage').then(m => ({ default: m.LeadsMetricsPage })))
const PartnerLayout = lazy(() => import('@/layouts/PartnerLayout').then(m => ({ default: m.PartnerLayout })))
const PartnerDashboard = lazy(() => import('@/pages/partner/PartnerDashboard').then(m => ({ default: m.PartnerDashboard })))
const PartnerClientsPage = lazy(() => import('@/pages/partner/PartnerClientsPage').then(m => ({ default: m.PartnerClientsPage })))
const PartnerBillingPage = lazy(() => import('@/pages/partner/PartnerBillingPage').then(m => ({ default: m.PartnerBillingPage })))
const PartnerLeadsPage = lazy(() => import('@/pages/partner/PartnerLeadsPage').then(m => ({ default: m.PartnerLeadsPage })))
const PartnerCommissionsPage = lazy(() => import('@/pages/partner/PartnerCommissionsPage').then(m => ({ default: m.PartnerCommissionsPage })))

// Personal Routes
const PersonalLayout = lazy(() => import('@/layouts/PersonalLayout').then(m => ({ default: m.PersonalLayout })))
const PersonalAuthGuard = lazy(() => import('@/components/PersonalAuthGuard').then(m => ({ default: m.PersonalAuthGuard })))
const PersonalDashboardPage = lazy(() => import('@/pages/fisica/PersonalDashboardPage').then(m => ({ default: m.PersonalDashboardPage })))
const PersonalMovementsPage = lazy(() => import('@/pages/fisica/PersonalMovementsPage').then(m => ({ default: m.PersonalMovementsPage })))
const PersonalAccountsPage = lazy(() => import('@/pages/fisica/PersonalAccountsPage').then(m => ({ default: m.PersonalAccountsPage })))
const PersonalReconciliationPage = lazy(() => import('@/pages/fisica/PersonalReconciliationPage').then(m => ({ default: m.PersonalReconciliationPage })))
const PersonalReportsPage = lazy(() => import('@/pages/fisica/PersonalReportsPage').then(m => ({ default: m.PersonalReportsPage })))
const PersonalProfilePage = lazy(() => import('@/pages/fisica/PersonalProfilePage').then(m => ({ default: m.PersonalProfilePage })))
const MobileLoginPage = lazy(() => import('@/pages/fisica/MobileLoginPage').then(m => ({ default: m.MobileLoginPage })))
const MobileRegisterPage = lazy(() => import('@/pages/fisica/MobileRegisterPage').then(m => ({ default: m.MobileRegisterPage })))
const MobileTwoFactorPage = lazy(() => import('@/pages/fisica/MobileTwoFactorPage').then(m => ({ default: m.MobileTwoFactorPage })))
const PersonalApresentacaoPage = lazy(() => import('@/pages/fisica/PersonalApresentacaoPage').then(m => ({ default: m.PersonalApresentacaoPage })))
const PersonalCategoriesPage = lazy(() => import('@/pages/fisica/PersonalCategoriesPage').then(m => ({ default: m.PersonalCategoriesPage })))
const PersonalBankAccountsPage = lazy(() => import('@/pages/fisica/PersonalBankAccountsPage').then(m => ({ default: m.PersonalBankAccountsPage })))
const PersonalTagsPage = lazy(() => import('@/pages/fisica/PersonalTagsPage').then(m => ({ default: m.PersonalTagsPage })))
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage').then(m => ({ default: m.MaintenancePage })))
const PaywallPage = lazy(() => import('@/pages/PaywallPage').then(m => ({ default: m.PaywallPage })))
const MasterBillingDashboard = lazy(() => import('@/pages/MasterBillingDashboard').then(m => ({ default: m.MasterBillingDashboard })))

/** Guard: só permite acesso se a empresa ativa for o Tenant Titular */
export function TitularTenantRoute({ children }: { children: ReactNode }) {
  const { isTitularTenant, loadingCompanies } = useCompany()
  if (loadingCompanies) return null
  if (!isTitularTenant) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/** Guard: só permite acesso se a empresa ativa for a empresa mestre */
function MasterRoute({ children }: { children: ReactNode }) {
  const { companies, loadingCompanies } = useCompany()
  if (loadingCompanies) return null
  const hasMasterAccess = companies.some(c => c.is_master === true && (c.user_role === 'owner' || c.user_role === 'admin'))
  return hasMasterAccess ? <>{children}</> : <Navigate to="/dashboard" replace />
}


/** Guard: só permite acesso se o usuário for investidor em ao menos uma empresa, ou owner/admin */
function InvestorRoute({ children }: { children: ReactNode }) {
  const { companies, loadingCompanies, activeCompany } = useCompany()
  if (loadingCompanies) return null
  const role = activeCompany?.user_role
  const hasAccess = role === 'owner' || role === 'admin' || companies.some(c => c.user_role === 'reader')
  return hasAccess ? <>{children}</> : <Navigate to="/dashboard" replace />
}

function PlanRequiredListener() {
  const navigate = useNavigate()
  useEffect(() => {
    const handlePlanRequired = () => {
      navigate('/paywall')
    }
    window.addEventListener('plan:required', handlePlanRequired)
    return () => {
      window.removeEventListener('plan:required', handlePlanRequired)
    }
  }, [navigate])
  return null
}

function App() {

  return (
    <BrowserRouter>
      <ConnectionStatusIndicator />
      <ThemeProvider>
        <AuthProvider>
          <Suspense fallback={null}>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/bpo-financeiro" element={<BpoFinanceiroPage />} />
          <Route path="/bpo-financeiro/:subserviceSlug" element={<BpoSubservicePage />} />
          <Route path="/quem-somos" element={<AboutUsPage />} />
          <Route path="/plataforma-seven" element={<PlatformPage />} />
          <Route path="/bpo-financeiro-para-clinicas" element={<BpoSegmentPage />} />
          <Route path="/bpo-financeiro-para-restaurantes" element={<BpoSegmentPage />} />
          <Route path="/bpo-financeiro-para-e-commerce" element={<BpoSegmentPage />} />
          <Route path="/bpo-financeiro-para-startups" element={<BpoSegmentPage />} />
          <Route path="/bpo-financeiro-para-escritorios-de-advocacia" element={<BpoSegmentPage />} />
          <Route path="/bpo-financeiro-para-construtoras" element={<BpoSegmentPage />} />
          <Route path="/bpo-financeiro-para-franquias" element={<BpoSegmentPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/apresentacao" element={<PersonalApresentacaoPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/privacidade" element={<PrivacyPolicyPage />} />
          <Route path="/termos" element={<TermsOfUsePage />} />
          <Route path="/exclusao-conta" element={<AccountDeletionPublicPage />} />
          <Route path="/exclusao-conta/confirm" element={<AccountDeletionConfirmPage />} />
          <Route path="/exclusao-conta/confirmar" element={<AccountDeletionConfirmPage />} />
          <Route path="/exclusao-conta/status" element={<AccountDeletionPublicPage defaultTab="status" />} />
          <Route path="/delete-account" element={<AccountDeletionPublicPage />} />
          <Route path="/solicitar-exclusao" element={<AccountDeletionPublicPage />} />
          <Route path="/delete-account/confirm" element={<AccountDeletionConfirmPage />} />
          <Route path="/delete-account/status" element={<AccountDeletionPublicPage defaultTab="status" />} />
          <Route path="/manutencao" element={<MaintenancePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/fisica/login" element={<MobileLoginPage />} />
          <Route path="/fisica/cadastro" element={<MobileRegisterPage />} />
          <Route path="/fisica/validar-codigo" element={<MobileTwoFactorPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/contato" element={<ContactPage />} />
                    <Route path="/onboarding/:token" element={<OnboardingFormPage />} />
          <Route
            path="/superadmin/onboarding"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <MasterRoute>
                    <AppLayout>
                      <OnboardingManagementPage />
                    </AppLayout>
                  </MasterRoute>
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/onboarding/:formId"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <MasterRoute>
                    <AppLayout>
                      <OnboardingDetailPage />
                    </AppLayout>
                  </MasterRoute>
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <MasterRoute>
                    <AppLayout>
                      <SuperAdminDashboardPage />
                    </AppLayout>
                  </MasterRoute>
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/paywall"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <PaywallPage />
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/payments"
            element={<Navigate to="/financeiro/pagamentos-pendentes" replace />}
          />
          <Route
            path="/financeiro/pagamentos-pendentes"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <PlanRequiredListener />
                  <AppLayout>
                    <RequirePermission module="movements" action="view">
                      <PendingPayoutsPage />
                    </RequirePermission>
                  </AppLayout>
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <PlanRequiredListener />
                  <AppLayout>
                    <Suspense fallback={null}>
                    <Routes>
                      <Route path="" element={<RequirePermission module="dashboard" action="view"><DashboardPage /></RequirePermission>} />
                      <Route
                        path="integration-logs"
                        element={<MasterRoute><RequirePermission module="integrations" action="view"><IntegrationLogsPage /></RequirePermission></MasterRoute>}
                      />
                       <Route
                        path="system-jobs"
                        element={<MasterRoute><RequirePermission module="system" action="view"><SystemJobLogsPage /></RequirePermission></MasterRoute>}
                      />
                      <Route
                        path="system-documents"
                        element={<MasterRoute><RequirePermission module="system" action="edit"><SystemDocumentsPage /></RequirePermission></MasterRoute>}
                      />
                      <Route
                        path="contact-submissions"
                        element={<MasterRoute><RequirePermission module="system" action="view"><ContactSubmissionsPage /></RequirePermission></MasterRoute>}
                      />
                      <Route
                        path="admin/leads-metrics"
                        element={<MasterRoute><LeadsMetricsPage /></MasterRoute>}
                      />
                      <Route
                        path="admin/user-accesses"
                        element={<MasterRoute><RequirePermission module="system" action="edit"><UsersAccessPage /></RequirePermission></MasterRoute>}
                      />
                      <Route
                        path="admin/schema-updates"
                        element={<MasterRoute><RequirePermission module="system" action="edit"><MasterSchemaUpdatesPage /></RequirePermission></MasterRoute>}
                      />
                      <Route
                        path="plans"
                        element={<TitularTenantRoute><RequirePermission module="plans" action="view"><PlansPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="plans/new"
                        element={<TitularTenantRoute><RequirePermission module="plans" action="create"><PlanFormPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="plans/:id/edit"
                        element={<TitularTenantRoute><RequirePermission module="plans" action="edit"><PlanFormPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="billing"
                        element={<RequirePermission module="billing" action="view"><BillingDashboardPage /></RequirePermission>}
                      />
                      <Route
                        path="admin/billing"
                        element={<MasterRoute><MasterBillingDashboard /></MasterRoute>}
                      />
                      <Route path="hr" element={<RequirePermission module="hr" action="view"><EmployeeListPage /></RequirePermission>} />
                      <Route path="hr/new" element={<RequirePermission module="hr" action="create"><EmployeeFormPage /></RequirePermission>} />
                      <Route path="hr/:id" element={<RequirePermission module="hr" action="view"><EmployeeDetailsPage /></RequirePermission>} />
                      <Route path="hr/:id/edit" element={<RequirePermission module="hr" action="edit"><EmployeeFormPage /></RequirePermission>} />
                      <Route
                        path="companies"
                        element={<TitularTenantRoute><RequirePermission module="companies" action="view"><CompaniesPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="companies/new"
                        element={<TitularTenantRoute><RequirePermission module="companies" action="create"><CompanyFormPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="companies/:companyId"
                        element={<TitularTenantRoute><RequirePermission module="companies" action="view"><CompanyDetailPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="companies/:companyId/edit"
                        element={<TitularTenantRoute><RequirePermission module="companies" action="edit"><CompanyFormPage /></RequirePermission></TitularTenantRoute>}
                      />
                      <Route
                        path="companies/:companyId/users/new"
                        element={<RequirePermission module="users" action="create"><UserFormPage /></RequirePermission>}
                      />
                      <Route
                        path="companies/:companyId/users/:id/edit"
                        element={<RequirePermission module="users" action="edit"><UserFormPage /></RequirePermission>}
                      />
                      <Route
                        path="company/edit"
                        element={<RequirePermission module="finance_settings" action="edit"><CompanyFormPage /></RequirePermission>}
                      />
                      {/* Category routes */}
                      <Route path="categories" element={<TitularTenantRoute><MasterRoute><RequirePermission module="categories" action="view"><CategoriesPage /></RequirePermission></MasterRoute></TitularTenantRoute>} />
                      <Route path="categories/new" element={<MasterRoute><RequirePermission module="categories" action="create"><CategoryFormPage /></RequirePermission></MasterRoute>} />
                      <Route path="categories/custom" element={<RequirePermission module="categories" action="view"><CompanyCategoriesPage /></RequirePermission>} />
                      <Route path="categories/custom/new" element={<RequirePermission module="categories" action="create"><CompanyCategoryFormPage /></RequirePermission>} />
                      <Route path="categories/:id/edit" element={<MasterRoute><RequirePermission module="categories" action="edit"><CategoryFormPage /></RequirePermission></MasterRoute>} />
                      <Route path="clients" element={<RequirePermission module="clients" action="view"><ClientsPage /></RequirePermission>} />
                      <Route path="clients/new" element={<RequirePermission module="clients" action="create"><ClientFormPage /></RequirePermission>} />
                      <Route path="clients/:id" element={<RequirePermission module="clients" action="view"><ClientDetailsPage /></RequirePermission>} />
                      <Route path="clients/:id/edit" element={<RequirePermission module="clients" action="edit"><ClientFormPage /></RequirePermission>} />

                      <Route path="partners" element={<RequirePermission module="partners" action="view"><PartnersPage /></RequirePermission>} />
                      <Route path="partners/new" element={<RequirePermission module="partners" action="create"><PartnerFormPage /></RequirePermission>} />
                      <Route path="partners/:id" element={<RequirePermission module="partners" action="view"><PartnerDetailsPage /></RequirePermission>} />
                      <Route path="partners/:id/edit" element={<RequirePermission module="partners" action="edit"><PartnerFormPage /></RequirePermission>} />
                      <Route path="partner-leads" element={<RequirePermission module="referrals" action="view"><LeadsPage /></RequirePermission>} />
                      <Route path="partner-leads/:id" element={<RequirePermission module="referrals" action="edit"><LeadFormPage /></RequirePermission>} />

                      <Route path="users" element={<RequirePermission module="users" action="view"><UsersPage /></RequirePermission>} />
                      <Route path="users/new" element={<RequirePermission module="users" action="create"><UserFormPage /></RequirePermission>} />
                      <Route path="users/:id/edit" element={<RequirePermission module="users" action="edit"><UserFormPage /></RequirePermission>} />
                      <Route path="assets" element={<RequirePermission module="assets" action="view"><AssetsPage /></RequirePermission>} />
                      <Route path="assets/new" element={<RequirePermission module="assets" action="create"><AssetFormPage /></RequirePermission>} />
                      <Route path="assets/:id/edit" element={<RequirePermission module="assets" action="edit"><AssetFormPage /></RequirePermission>} />
                      <Route path="movements" element={<RequirePermission module="movements" action="view"><MovementsPage /></RequirePermission>} />
                      <Route path="payments" element={<Navigate to="/financeiro/pagamentos-pendentes" replace />} />
                      <Route path="pagamentos-pendentes" element={<RequirePermission module="movements" action="view"><PendingPayoutsPage /></RequirePermission>} />
                      <Route path="movements/new" element={<RequirePermission module="movements" action="create"><MovementFormPage /></RequirePermission>} />
                      <Route path="movements/:id/edit" element={<RequirePermission module="movements" action="edit"><MovementFormPage /></RequirePermission>} />
                      <Route path="calendar" element={<RequirePermission module="calendar" action="view"><CalendarPage /></RequirePermission>} />
                      <Route path="reconciliation" element={<RequirePermission module="reconciliation" action="view"><ReconciliationPage /></RequirePermission>} />
                      <Route path="reconciliation-v2" element={<MasterRoute><RequirePermission module="reconciliation" action="view"><ReconciliationV2Page /></RequirePermission></MasterRoute>} />
                      <Route path="commissions" element={<RequirePermission module="commissions" action="view"><CommissionsPage /></RequirePermission>} />
                      <Route path="invoices" element={<RequirePermission module="invoices" action="view"><InvoicesPage /></RequirePermission>} />
                      <Route path="commissions/new" element={<RequirePermission module="commissions" action="create"><CommissionFormPage /></RequirePermission>} />
                      <Route path="commissions/:id/edit" element={<RequirePermission module="commissions" action="edit"><CommissionFormPage /></RequirePermission>} />
                      <Route path="bank-accounts" element={<RequirePermission module="bank_accounts" action="view"><BankAccountsPage /></RequirePermission>} />
                      <Route path="bank-accounts/new" element={<RequirePermission module="bank_accounts" action="create"><BankAccountFormPage /></RequirePermission>} />
                      <Route path="bank-accounts/:id" element={<RequirePermission module="bank_accounts" action="view"><BankAccountDetailsPage /></RequirePermission>} />
                      <Route path="bank-accounts/:id/edit" element={<RequirePermission module="bank_accounts" action="edit"><BankAccountFormPage /></RequirePermission>} />
                      <Route path="bank-statements" element={<RequirePermission module="bank_statements" action="view"><BankStatementsPage /></RequirePermission>} />
                      <Route path="avantti" element={<RequirePermission module="movements" action="view"><AvanttiDashboardPage /></RequirePermission>} />
                       <Route path="cost-centers" element={<RequirePermission module="movements" action="view"><CostCentersPage /></RequirePermission>} />
                       <Route path="cost-centers/new" element={<RequirePermission module="movements" action="create"><CostCenterFormPage /></RequirePermission>} />
                       <Route path="cost-centers/:id" element={<RequirePermission module="movements" action="view"><CostCenterDetailsPage /></RequirePermission>} />
                       <Route path="cost-centers/:id/edit" element={<RequirePermission module="movements" action="edit"><CostCenterFormPage /></RequirePermission>} />
                      
                      <Route path="payment-methods" element={<RequirePermission module="payment_methods" action="view"><PaymentMethodsPage /></RequirePermission>} />
                      <Route path="payment-methods/new" element={<RequirePermission module="payment_methods" action="create"><PaymentMethodFormPage /></RequirePermission>} />
                      <Route path="payment-methods/:id" element={<RequirePermission module="payment_methods" action="view"><PaymentMethodDetailsPage /></RequirePermission>} />
                      <Route path="payment-methods/:id/edit" element={<RequirePermission module="payment_methods" action="edit"><PaymentMethodFormPage /></RequirePermission>} />
                      
                      <Route path="services" element={<RequirePermission module="services" action="view"><ServicesPage /></RequirePermission>} />
                      <Route path="services/new" element={<RequirePermission module="services" action="create"><ServiceFormPage /></RequirePermission>} />
                      <Route path="services/:id" element={<RequirePermission module="services" action="view"><ServiceDetailsPage /></RequirePermission>} />
                      <Route path="services/:id/edit" element={<RequirePermission module="services" action="edit"><ServiceFormPage /></RequirePermission>} />
                      <Route path="contracts" element={<RequirePermission module="contracts" action="view"><ContractsPage /></RequirePermission>} />
                      <Route path="reports" element={<RequirePermission module="reports" action="view"><ReportsPage /></RequirePermission>} />
                      <Route path="reports/fluxo-caixa" element={<RequirePermission module="reports" action="view"><CashFlowPage /></RequirePermission>} />
                      <Route path="investor" element={<InvestorRoute><InvestorDashboardPage /></InvestorRoute>} />
                      <Route path="reports/mensal" element={<RequirePermission module="monthly" action="view"><MensalPage /></RequirePermission>} />
                      <Route path="reports/reconciled" element={<RequirePermission module="reports" action="view"><ReconciledReportPage /></RequirePermission>} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="email-settings" element={<TitularTenantRoute><RequirePermission module="finance_settings" action="view"><EmailSettingsPage /></RequirePermission></TitularTenantRoute>} />
                      <Route path="whatsapp-settings" element={<RequirePermission module="company" action="edit"><WhatsappSettingsPage /></RequirePermission>} />
                      <Route path="branding" element={<TitularTenantRoute><RequirePermission module="company" action="edit"><BrandingPage /></RequirePermission></TitularTenantRoute>} />
                      <Route path="legal-settings" element={<TitularTenantRoute><RequirePermission module="company" action="edit"><LegalSettingsPage /></RequirePermission></TitularTenantRoute>} />
                      <Route path="asaas-settings" element={<TitularTenantRoute><RequirePermission module="finance_settings" action="edit"><AsaasSettingsPage /></RequirePermission></TitularTenantRoute>} />
                      <Route path="gateways/asaas" element={<TitularTenantRoute><RequirePermission module="finance_settings" action="edit"><AsaasSettingsPage /></RequirePermission></TitularTenantRoute>} />
                      <Route path="milestones" element={<RequirePermission module="milestones" action="view"><MilestonesPage /></RequirePermission>} />
                      <Route path="milestones/new" element={<RequirePermission module="milestones" action="create"><MilestoneFormPage /></RequirePermission>} />
                      <Route path="milestones/:id/edit" element={<RequirePermission module="milestones" action="edit"><MilestoneFormPage /></RequirePermission>} />
                      {/* Rota sem MasterRoute — para usuários que acabaram de se cadastrar e não têm empresa */}
                      <Route path="company-setup" element={<CompanyFormPage />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    </Suspense>
                  </AppLayout>
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner/*"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <PartnerLayout>
                    <Routes>
                      <Route path="" element={<PartnerDashboard />} />
                      <Route path="clients" element={<PartnerClientsPage />} />
                      <Route path="billing" element={<PartnerBillingPage />} />
                      <Route path="leads" element={<PartnerLeadsPage />} />
                      <Route path="commissions" element={<PartnerCommissionsPage />} />
                      <Route path="*" element={<Navigate to="/partner" replace />} />
                    </Routes>
                  </PartnerLayout>
                </CompanyProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="/fisica"
            element={
              <PersonalAuthGuard>
                <CompanyProvider>
                  <PersonalLayout />
                </CompanyProvider>
              </PersonalAuthGuard>
            }
          >
            <Route index element={<Navigate to="/fisica/dashboard" replace />} />
            <Route path="dashboard" element={<PersonalDashboardPage />} />
            <Route path="lancamentos" element={<PersonalMovementsPage />} />
            <Route path="contas" element={<PersonalAccountsPage />} />
            <Route path="conciliacao" element={<PersonalReconciliationPage />} />
            <Route path="relatorios" element={<PersonalReportsPage />} />
            <Route path="perfil" element={<PersonalProfilePage />} />
            <Route path="categorias" element={<PersonalCategoriesPage />} />
            <Route path="contas-bancarias" element={<PersonalBankAccountsPage />} />
            <Route path="etiquetas" element={<PersonalTagsPage />} />
            <Route path="apresentacao" element={<PersonalApresentacaoPage />} />
            <Route path="*" element={<Navigate to="/fisica/dashboard" replace />} />
          </Route>
        </Routes>
          </Suspense>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}

export default App

